import React, { memo, useMemo } from 'react';
import { RichMarkdown } from '../markdown';
import StructuredResponseRenderer from './StructuredResponseRenderer';
import { hasStructuredBlocks, normalizeResponseBlocks } from './structuredBlockUtils';

function AssistantResponseContent({
    answer,
    responseBlocks,
    completedBlocks = [],
    pendingMarkdown,
    streaming = false,
    streamingComplete = true,
    citations = [],
    chunks = [],
    citationScope = '',
    onCitationClick,
    onViewEvidence,
    className = '',
    consultantLayout = true,
    reportActions = null,
}) {
    const blocks = useMemo(() => normalizeResponseBlocks(responseBlocks), [responseBlocks]);
    const useStructured = blocks.length > 0;
    const isStreamingActive = streaming && !streamingComplete;
    const markdownContent = isStreamingActive
        ? pendingMarkdown ?? answer
        : answer;

    if (isStreamingActive) {
        const renderedBlocks = normalizeResponseBlocks(completedBlocks);
        return (
            <div className={className || undefined}>
                {renderedBlocks.length > 0 ? (
                    <StructuredResponseRenderer
                        blocks={renderedBlocks}
                        fallbackContent=""
                        citations={citations}
                        chunks={chunks}
                        citationScope={citationScope}
                        onCitationClick={onCitationClick}
                        onViewEvidence={onViewEvidence}
                        consultantLayout={false}
                    />
                ) : null}
                <RichMarkdown
                    content={markdownContent}
                    citations={citations}
                    chunks={chunks}
                    citationScope={citationScope}
                    onCitationClick={onCitationClick}
                    streaming
                    streamingComplete={false}
                    className={renderedBlocks.length > 0 ? undefined : className}
                />
            </div>
        );
    }

    if (!useStructured) {
        return (
            <RichMarkdown
                content={answer}
                citations={citations}
                chunks={chunks}
                citationScope={citationScope}
                onCitationClick={onCitationClick}
                streaming={streaming}
                streamingComplete={streamingComplete}
                className={className}
            />
        );
    }

    return (
        <StructuredResponseRenderer
            blocks={blocks}
            fallbackContent={answer}
            citations={citations}
            chunks={chunks}
            citationScope={citationScope}
            onCitationClick={onCitationClick}
            onViewEvidence={onViewEvidence}
            consultantLayout={consultantLayout}
            reportActions={reportActions}
            className={className}
        />
    );
}

export default memo(AssistantResponseContent, (prev, next) => {
    if (prev.streaming || next.streaming) {
        return (
            prev.answer === next.answer &&
            prev.pendingMarkdown === next.pendingMarkdown &&
            prev.completedBlocks === next.completedBlocks &&
            prev.streaming === next.streaming &&
            prev.streamingComplete === next.streamingComplete &&
            prev.responseBlocks === next.responseBlocks &&
            prev.citations === next.citations &&
            prev.chunks === next.chunks &&
            prev.citationScope === next.citationScope &&
            prev.onCitationClick === next.onCitationClick &&
            prev.onViewEvidence === next.onViewEvidence &&
            prev.className === next.className &&
            prev.consultantLayout === next.consultantLayout &&
            prev.reportActions === next.reportActions
        );
    }

    if (!hasStructuredBlocks(prev.responseBlocks) && !hasStructuredBlocks(next.responseBlocks)) {
        return (
            prev.answer === next.answer &&
            prev.citations === next.citations &&
            prev.chunks === next.chunks &&
            prev.citationScope === next.citationScope &&
            prev.onCitationClick === next.onCitationClick &&
            prev.onViewEvidence === next.onViewEvidence &&
            prev.className === next.className &&
            prev.streamingComplete === next.streamingComplete
        );
    }
    return false;
});

export { hasStructuredBlocks };
